import { getAllSubscriptions } from './subscriptionService';
import { UnifiedSubscription, SubscriptionMetrics } from '../types/subscription';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../config/firebase';

const functions = getFunctions(app);

interface StripeSubscription {
  id: string;
  status: string;
  customer: string;
  items: {
    data: Array<{
      price: {
        unit_amount: number;
        currency: string;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
  current_period_start: number;
  current_period_end: number;
  trial_end?: number;
  cancel_at_period_end: boolean;
  created: number;
}

/**
 * Known plan prices (monthly equivalent in USD).
 * Used as fallback when amount is not stored in Firestore (e.g. mobile subscriptions).
 */
const PLAN_MONTHLY_PRICES: Record<string, number> = {
  premium_monthly: 9.99,
  premium_quarterly: 24.99,
  premium_yearly: 59.99,
  premium_annual: 59.99,
};

/**
 * Normalizes a subscription amount to its monthly equivalent.
 * Quarterly is divided by 3, annual/yearly by 12.
 */
export function normalizeAmountToMonthly(amount: number, planId?: string): number {
  const plan = (planId || '').toLowerCase();

  if (!amount || amount === 0) {
    const knownPrice = PLAN_MONTHLY_PRICES[plan];
    if (!knownPrice) return 0;
    amount = knownPrice;
  }

  if (plan.includes('annual') || plan.includes('yearly')) {
    return Math.round((amount / 12) * 100) / 100;
  }
  if (plan.includes('quarterly')) {
    return Math.round((amount / 3) * 100) / 100;
  }
  return amount;
}

export const aggregateSubscriptions = async (): Promise<SubscriptionMetrics> => {
  try {
    const firestoreSubscriptions = await getAllSubscriptions();

    let stripeSubscriptions: StripeSubscription[] = [];
    try {
      const getStripeSubscriptions = httpsCallable(functions, 'adminStripeProxy');
      const result = await getStripeSubscriptions({ action: 'listSubscriptions' });
      stripeSubscriptions = (result.data as any)?.subscriptions || [];
    } catch (error: any) {
      console.warn('Failed to fetch Stripe subscriptions:', error);
      if (error?.code === 'internal') {
        console.warn('Stripe API connection issue - continuing with Firestore data only');
      }
      stripeSubscriptions = [];
    }

    const allSubscriptions: UnifiedSubscription[] = [...firestoreSubscriptions];

    for (const stripeSub of stripeSubscriptions) {
      const exists = firestoreSubscriptions.some(
        (sub) => sub.stripeSubscriptionId === stripeSub.id
      );
      
      if (!exists && stripeSub.items.data.length > 0) {
        const price = stripeSub.items.data[0].price;
        const amount = price.unit_amount ? price.unit_amount / 100 : 0;
        const isTrial = stripeSub.trial_end && stripeSub.trial_end * 1000 > Date.now();
        
        allSubscriptions.push({
          userId: stripeSub.customer,
          email: '',
          platform: 'stripe',
          status: (isTrial ? 'trialing' : stripeSub.status) as any,
          planId: 'stripe_plan',
          amount,
          currency: price.currency || 'usd',
          currentPeriodStart: new Date(stripeSub.current_period_start * 1000),
          currentPeriodEnd: new Date(stripeSub.current_period_end * 1000),
          isTrial: !!isTrial,
          trialEndDate: stripeSub.trial_end
            ? new Date(stripeSub.trial_end * 1000)
            : undefined,
          cancelAtPeriodEnd: stripeSub.cancel_at_period_end,
          stripeSubscriptionId: stripeSub.id,
          createdAt: new Date(stripeSub.created * 1000),
          updatedAt: new Date(),
        });
      }
    }

    const now = new Date();
    const totalSubscriptions = allSubscriptions.length;
    const activeSubscriptions = allSubscriptions.filter(
      (sub) => sub.status === 'active'
    ).length;
    const trialingSubscriptions = allSubscriptions.filter(
      (sub) => sub.status === 'trialing'
    ).length;
    const canceledSubscriptions = allSubscriptions.filter(
      (sub) => sub.status === 'canceled'
    ).length;

    let totalMRR = 0;
    
    for (const sub of allSubscriptions) {
      const isPaidActive = sub.status === 'active' && !sub.isTrial;
      const isTrialingWithValue = sub.status === 'trialing';
      const isCanceledButStillInPeriod = sub.cancelAtPeriodEnd && sub.currentPeriodEnd > now && sub.status === 'active';

      if (isPaidActive || isTrialingWithValue || isCanceledButStillInPeriod) {
        totalMRR += normalizeAmountToMonthly(sub.amount, sub.planId);
      }
    }

    const platformDistribution = {
      stripe: allSubscriptions.filter((sub) => sub.platform === 'stripe').length,
      ios: allSubscriptions.filter((sub) => sub.platform === 'ios').length,
      android: allSubscriptions.filter((sub) => sub.platform === 'android').length,
    };

    const currentTrials = allSubscriptions.filter((sub) => sub.isTrial || sub.status === 'trialing');
    const convertedFromTrial = allSubscriptions.filter(
      (sub) => !sub.isTrial && sub.status === 'active' && sub.trialEndDate && sub.trialEndDate < now
    );
    const totalTrialEver = currentTrials.length + convertedFromTrial.length;
    const trialConversionRate =
      totalTrialEver > 0 ? (convertedFromTrial.length / totalTrialEver) * 100 : 0;

    const churnRate =
      totalSubscriptions > 0
        ? (canceledSubscriptions / totalSubscriptions) * 100
        : 0;

    return {
      totalSubscriptions,
      activeSubscriptions,
      trialingSubscriptions,
      canceledSubscriptions,
      totalMRR,
      platformDistribution,
      trialConversionRate,
      churnRate,
    };
  } catch (error) {
    console.error('Error aggregating subscriptions:', error);
    throw error;
  }
};

export const getCrossPlatformSubscriptions = async (
  email: string
): Promise<UnifiedSubscription[]> => {
  const allSubscriptions = await getAllSubscriptions();
  const normalizedEmail = email.toLowerCase().trim();
  
  return allSubscriptions.filter(
    (sub) => sub.email.toLowerCase().trim() === normalizedEmail
  );
};
